import React, { useState, useRef, useEffect } from "react";

//font awsome icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faM, faSearch } from "@fortawesome/free-solid-svg-icons";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { faSun } from "@fortawesome/free-solid-svg-icons";
import { faMoon } from "@fortawesome/free-solid-svg-icons";
import { faBookmark } from "@fortawesome/free-solid-svg-icons";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

const Browser = ({
  initialUrl = "https://satisfyingclock.netlify.app",
  width = "100%",
  height = "100%",
  onUrlChange = null,
  customBookmarks = [],
  theme = "light",
  onThemeToggle = null,
}) => {
  const [currentUrl, setCurrentUrl] = useState(initialUrl);
  const [inputUrl, setInputUrl] = useState(initialUrl);
  const [history, setHistory] = useState([initialUrl]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSecure, setIsSecure] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [browserTheme, setbrowserTheme] = useState(theme);
  const iframeRef = useRef(null);

  const defaultBookmarks = [
    { name: "W3Schools", url: "https://www.w3schools.com/" },
    { name: "Wikipedia", url: "https://wikipedia.org" },
    {
      name: "Youtube",
      url: "https://www.youtube.com/embed/dQw4w9WgXcQ/?autoplay=1",
    },
    ...customBookmarks,
  ];

  const [bookmarks, setBookmarks] = useState(defaultBookmarks);

  useEffect(() => {
    setIsSecure(currentUrl.startsWith("https://"));
    if (onUrlChange) {
      onUrlChange(currentUrl);
    }
  }, [currentUrl, onUrlChange]);

  const isValidUrl = (url) => {
    try {
      // Add protocol if missing
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = "https://" + url;
      }
      new URL(url);
      return url;
    } catch {
      // If not a valid URL, treat as search query
      return `https://www.google.com/search?q=${encodeURIComponent(url)}`;
    }
  };

  const navigateToUrl = (url) => {
    const validUrl = isValidUrl(url);
    setIsLoading(true);
    setCurrentUrl(validUrl);
    setInputUrl(validUrl);

    // Update history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(validUrl);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleUrlSubmit = (e) => {
    e.preventDefault();
    if (inputUrl.trim()) {
      navigateToUrl(inputUrl.trim());
    }
  };

  const handleBack = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const url = history[newIndex];
      setCurrentUrl(url);
      setInputUrl(url);
      setIsLoading(true);
    }
  };

  const handleForward = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const url = history[newIndex];
      setCurrentUrl(url);
      setInputUrl(url);
      setIsLoading(true);
    }
  };

  const handleRefresh = () => {
    setIsLoading(true);
    if (iframeRef.current) {
      iframeRef.current.src = currentUrl;
    }
  };

  const handleHome = () => {
    navigateToUrl(initialUrl);
  };

  const handleBookmarkClick = (url) => {
    navigateToUrl(url);
    setShowBookmarks(false);
  };

  const addBookmark = () => {
    const name = prompt("Bookmark name:", getDomainFromUrl(currentUrl));
    if (name) {
      setBookmarks((prev) => [...prev, { name: name.trim(), url: currentUrl }]);
    }
  };

  const getDomainFromUrl = (url) => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
  };

  const toggleTheme = () => {
    const newTheme = browserTheme === "dark" ? "light" : "dark";
    setbrowserTheme(newTheme);
    if (onThemeToggle) {
      onThemeToggle(newTheme);
    }
  };

  const browserStyles = {
    light: {
      container: {
        backgroundColor: "#f8f9fa",
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
      toolbar: {
        backgroundColor: "#ffffff",
        borderBottom: "1px solid #dee2e6",
        color: "#333",
      },
      urlBar: {
        backgroundColor: "#ffffff",
        border: "1px solid #ced4da",
        color: "#333",
      },
      button: {
        backgroundColor: "#f8f9fa",
        border: "1px solid #ced4da",
        color: "#333",
      },
      bookmarkPanel: {
        backgroundColor: "#ffffff",
        border: "1px solid #dee2e6",
      },
    },
    dark: {
      container: {
        backgroundColor: "#2d3748",
        border: "1px solid #4a5568",
        borderRadius: "8px",
      },
      toolbar: {
        backgroundColor: "#1a202c",
        borderBottom: "1px solid #4a5568",
        color: "#e2e8f0",
      },
      urlBar: {
        backgroundColor: "#2d3748",
        border: "1px solid #4a5568",
        color: "#e2e8f0",
      },
      button: {
        backgroundColor: "#2d3748",
        border: "1px solid #4a5568",
        color: "#e2e8f0",
      },
      bookmarkPanel: {
        backgroundColor: "#1a202c",
        border: "1px solid #4a5568",
      },
    },
  };

  const currentTheme = browserStyles[browserTheme] || browserStyles.light;

  return (
    <div
      style={{
        width,
        height,
        display: "flex",
        flexDirection: "column",
        fontFamily: "system-ui, -apple-system, sans-serif",
        ...currentTheme.container,
      }}
    >
      {/* Browser Toolbar */}
      <div
        style={{
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          minHeight: "48px",
          ...currentTheme.toolbar,
        }}
      >
        {/* Navigation Buttons */}
        <div style={{ display: "flex", gap: "4px" }}>
          <button
            onClick={handleBack}
            disabled={historyIndex <= 0}
            style={{
              padding: "6px 8px",
              borderRadius: "4px",
              cursor: historyIndex <= 0 ? "not-allowed" : "pointer",
              opacity: historyIndex <= 0 ? 0.5 : 1,
              fontSize: "14px",
              ...currentTheme.button,
            }}
            title="Back"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <button
            onClick={handleForward}
            disabled={historyIndex >= history.length - 1}
            style={{
              padding: "6px 8px",
              borderRadius: "4px",
              cursor:
                historyIndex >= history.length - 1 ? "not-allowed" : "pointer",
              opacity: historyIndex >= history.length - 1 ? 0.5 : 1,
              fontSize: "14px",
              ...currentTheme.button,
            }}
            title="Forward"
          >
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
          <button
            onClick={handleRefresh}
            style={{
              padding: "6px 8px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              ...currentTheme.button,
            }}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faRefresh} />
          </button>
          <button
            onClick={handleHome}
            style={{
              padding: "6px 8px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              ...currentTheme.button,
            }}
            title="Home"
          >
            <FontAwesomeIcon icon={faHome} />
          </button>
        </div>

        {/* URL Bar */}
        <form
          onSubmit={handleUrlSubmit}
          style={{ flex: 1, display: "flex", gap: "8px" }}
        >
          <div
            style={{
              flex: 1,
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: "8px",
                fontSize: "14px",
                color: isSecure ? "#28a745" : "#ffc107",
                zIndex: 1,
              }}
            >
              {isSecure ? "🔒" : "⚠️"}
            </div>
            <input
              type="text"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              placeholder="Enter URL or search term..."
              style={{
                width: "100%",
                padding: "8px 8px 8px 28px",
                borderRadius: "20px",
                fontSize: "14px",
                outline: "none",
                ...currentTheme.urlBar,
              }}
            />
          </div>
          <button
            type="submit"
            style={{
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              ...currentTheme.button,
            }}
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </form>

        {/* Additional Controls */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            justifyContent: "space-evenly",
            alignItems: "center",
          }}
        >
          <button
            onClick={() => setShowBookmarks(!showBookmarks)}
            style={{
              width: "35px",
              height: "35px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              ...currentTheme.button,
            }}
            title="Bookmarks"
          >
            <FontAwesomeIcon icon={faStar} />
          </button>
          <button
            onClick={addBookmark}
            style={{
              width: "35px",
              height: "35px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              ...currentTheme.button,
            }}
            title="Add Bookmark"
          >
            <FontAwesomeIcon icon={faBookmark} />
          </button>
          <button
            onClick={toggleTheme}
            style={{
              width: "35px",
              height: "35px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              ...currentTheme.button,
            }}
            title="Toggle Theme"
          >
            {browserTheme === "dark" ? (
              <FontAwesomeIcon icon={faMoon} />
            ) : (
              <FontAwesomeIcon icon={faSun} />
            )}
          </button>
        </div>
      </div>

      {/* Bookmarks Panel */}
      {showBookmarks && (
        <div
          style={{
            padding: "8px",
            borderBottom: "1px solid #dee2e6",
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            maxHeight: "120px",
            overflow: "hidden",
            ...currentTheme.bookmarkPanel,
          }}
        >
          {bookmarks.map((bookmark, index) => (
            <button
              key={index}
              onClick={() => handleBookmarkClick(bookmark.url)}
              style={{
                padding: "4px 8px",
                borderRadius: "12px",
                fontSize: "12px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                ...currentTheme.button,
              }}
            >
              {bookmark.name}
            </button>
          ))}
        </div>
      )}

      {/* Loading Indicator */}
      {isLoading && (
        <div
          style={{
            padding: "8px 12px",
            fontSize: "12px",
            color: "#666",
            borderBottom: "1px solid #dee2e6",
            ...currentTheme.toolbar,
          }}
        >
          Loading: {getDomainFromUrl(currentUrl)}...
        </div>
      )}

      {/* Content Frame */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <iframe
          ref={iframeRef}
          src={currentUrl}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            borderRadius: "0 0 8px 8px",
          }}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          title="Browser Content"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation"
        />

        {/* Error Fallback */}
        {!isLoading && currentUrl && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "none", // Will be shown by CSS if iframe fails
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              backgroundColor: theme === "dark" ? "#2d3748" : "#f8f9fa",
              color: theme === "dark" ? "#e2e8f0" : "#333",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>🌐</div>
            <h3>Unable to load page</h3>
            <p style={{ textAlign: "center", margin: "8px 0", color: "#666" }}>
              The page at {getDomainFromUrl(currentUrl)} could not be loaded.
            </p>
            <button
              onClick={() =>
                navigateToUrl("https://satisfyingclock.netlify.app")
              }
              style={{
                marginTop: "16px",
                padding: "8px 16px",
                borderRadius: "4px",
                cursor: "pointer",
                ...currentTheme.button,
              }}
            >
              Go to default page
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Browser;
